import { z } from 'zod';
import { UuidSchema } from './common';
import { ChannelSchema } from './messaging';
import { EmailDesignSchema } from '../emailDesign';

export const CAMPAIGN_STATUSES = [
  'draft',
  'scheduled',
  'sending',
  'sent',
  'canceled',
  'failed',
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/** Who receives a campaign. Basic: all / hand-picked / tag. Pro adds saved segments. */
export const CampaignAudienceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('all') }),
  z.object({ type: z.literal('contacts'), contactIds: z.array(UuidSchema).min(1).max(10000) }),
  z.object({
    type: z.literal('tags'),
    tags: z.array(z.string().min(1)).min(1),
    match: z.enum(['any', 'all']).default('any'),
  }),
  z.object({ type: z.literal('segment'), segmentId: UuidSchema }),
  /**
   * Follow-ups of a form (forms engine, FORMS_DESIGN §8.3): people of the form by answer status or
   * derived state, resolved at send time. Registered by the forms module (feature by form kind).
   */
  z.object({
    type: z.literal('form'),
    formId: UuidSchema,
    statuses: z
      .array(
        z.enum([
          'interested',
          'maybe',
          'not_interested',
          'going',
          'not_going',
          'waitlist',
          'responded',
          'no_reply',
          'not_reached',
          'not_sent',
          'checked_in',
          'not_checked_in',
        ]),
      )
      .min(1)
      .max(8),
  }),
]);
export type CampaignAudience = z.infer<typeof CampaignAudienceSchema>;

/** Delivery purpose of a campaign (`event_message` / `consent_confirmation` arrive in v1.1). */
export const CAMPAIGN_PURPOSES = ['campaign', 'event_message', 'consent_confirmation'] as const;
export type CampaignPurpose = (typeof CAMPAIGN_PURPOSES)[number];
/**
 * Options of a campaign linked to a form (`campaigns.form_options`). `reminder` marks the send
 * group a form reminder materialized (attendee reminders are bounded by the event start instead
 * of the RSVP deadline, skip invitation bookkeeping and honour the reminders opt-out).
 */
export interface CampaignFormOptions {
  excludeResponded?: boolean;
  reminder?: 'attendee' | 'non_responder';
  reminderId?: string;
}

export const SEND_GROUP_ROLES = ['primary', 'fallback'] as const;
export type SendGroupRole = (typeof SEND_GROUP_ROLES)[number];

export const CampaignStatsSchema = z.object({
  targeted: z.number().int().default(0),
  eligible: z.number().int().default(0),
  excluded: z.record(z.string(), z.number().int()).default({}),
  queued: z.number().int().default(0),
  sent: z.number().int().default(0),
  delivered: z.number().int().default(0),
  failed: z.number().int().default(0),
  opened: z.number().int().default(0),
  clicked: z.number().int().default(0),
  unsubscribed: z.number().int().default(0),
  creditsCharged: z.number().int().default(0),
  /** Messaging module additions (all optional for older rows). */
  accepted: z.number().int().default(0),
  bounced: z.number().int().default(0),
  suppressed: z.number().int().default(0),
  unknown: z.number().int().default(0),
  cancelled: z.number().int().default(0),
  /** Credits reserved for the send (estimate at confirmation). */
  creditsReserved: z.number().int().default(0),
  /** Why a scheduled/sending campaign failed (e.g. insufficient_credits). */
  error: z.string().optional(),
  /** Linked campaigns: personal form pages opened (recorded by the page; indicative). */
  pageOpened: z.number().int().optional(),
});
export type CampaignStats = z.infer<typeof CampaignStatsSchema>;

export const CreateCampaignRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    channel: ChannelSchema,
    audience: CampaignAudienceSchema,
    subject: z.string().max(200).optional(),
    /** Text message (SMS, plain email). Optional for designed emails (derived from the design). */
    body: z.string().max(20000).default(''),
    /** Designed email (email channel): block document or HTML (sanitized by the server). */
    design: EmailDesignSchema.nullable().optional(),
    locale: z.enum(['el', 'en']).optional(),
    templateId: UuidSchema.optional(),
    scheduledFor: z.iso.datetime({ offset: true }).optional(),
  })
  .superRefine((v, ctx) => {
    if (!(v.channel === 'email' && v.design) && !v.body.trim())
      ctx.addIssue({ code: 'custom', path: ['body'], message: 'Message is empty' });
  });
export type CreateCampaignRequest = z.input<typeof CreateCampaignRequestSchema>;

// ─── messaging module: campaign API ──────────────────────────────────────────

const IsoDateTime = z.iso.datetime({ offset: true });

export const UpdateCampaignRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    channel: ChannelSchema,
    audience: CampaignAudienceSchema,
    subject: z.string().max(200).nullable(),
    body: z.string().min(1).max(20000),
    /** null switches a designed email back to plain text. */
    design: EmailDesignSchema.nullable(),
    locale: z.enum(['el', 'en']),
  })
  .partial();
export type UpdateCampaignRequest = z.input<typeof UpdateCampaignRequestSchema>;

export const CampaignSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  channel: ChannelSchema,
  status: z.enum(CAMPAIGN_STATUSES),
  audience: CampaignAudienceSchema,
  subject: z.string().nullable(),
  body: z.string(),
  /** Designed email (email campaigns); null = plain text email / SMS. */
  design: EmailDesignSchema.nullable().default(null),
  locale: z.enum(['el', 'en']),
  scheduledFor: IsoDateTime.nullable(),
  startedAt: IsoDateTime.nullable(),
  sentAt: IsoDateTime.nullable(),
  canceledAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  estimatedUnits: z.number().int().nullable(),
  stats: CampaignStatsSchema,
  createdBy: z.string().nullable(),
  /** Prepared by the assistant (the composer asks the user to check text, links and audience). */
  aiGenerated: z.boolean().default(false),
  aiRunId: UuidSchema.nullable().default(null),
  /** Forms engine: the form this campaign invites to / follows up (null = ordinary campaign). */
  formId: UuidSchema.nullable().default(null),
  linkedForm: z
    .object({ id: UuidSchema, kind: z.enum(['interest', 'event']), title: z.string() })
    .nullable()
    .default(null),
  purpose: z.enum(CAMPAIGN_PURPOSES).default('campaign'),
  /** Never sent at/after this instant (form close / RSVP deadline / event start). */
  notAfter: IsoDateTime.nullable().default(null),
  sendGroupId: UuidSchema.nullable().default(null),
  sendGroupRole: z.enum(SEND_GROUP_ROLES).nullable().default(null),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignListResponseSchema = z.object({
  items: z.array(CampaignSchema),
  nextCursor: z.string().nullable(),
});

export const CampaignListQuerySchema = z.object({
  status: z.enum(['all', ...CAMPAIGN_STATUSES]).default('all'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const SendCampaignRequestSchema = z.object({
  /** Required when the estimate is flagged high-volume (second confirmation). */
  confirmHighVolume: z.boolean().default(false),
});

export const ScheduleCampaignRequestSchema = z.object({
  scheduledFor: IsoDateTime,
  confirmHighVolume: z.boolean().default(false),
});

export const EstimateRequestSchema = z.object({
  channel: ChannelSchema,
  audience: CampaignAudienceSchema,
  subject: z.string().max(200).optional(),
  body: z.string().max(20000).default(''),
  design: EmailDesignSchema.nullable().optional(),
  /** Forms engine: estimate as a campaign linked to this form (exact link length). */
  formId: UuidSchema.optional(),
});
export type EstimateRequest = z.input<typeof EstimateRequestSchema>;
